import { useRef, useState, type ComponentProps, type MouseEvent, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@scio3/ui';

type TruncatedTooltipProps = {
  /** The full text. Shown in the tooltip, and rendered in the trigger unless `children` is given. */
  text: string | null | undefined;
  /** Classes for the trigger span. `truncate` is always applied on top of these. */
  className?: string;
  /** How long the pointer must rest before it opens. */
  delayDuration?: number;
  side?: ComponentProps<typeof TooltipContent>['side'];
  align?: ComponentProps<typeof TooltipContent>['align'];
  /** Custom trigger content, when the visible text is not just `text` (badges, markup). */
  children?: ReactNode;
  /** The trigger span is the clickable surface on rows whose whole name opens something. */
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
  'data-testid'?: string;
};

/**
 * Text that truncates, with a tooltip carrying the full value — but ONLY when it is actually
 * cut off. A tooltip that repeats a label you can already read in full is noise, and it steals
 * hover from whatever is underneath.
 *
 * This pattern was hand-rolled in ten places before this component existed (asset rows, engage
 * rows, the device table, the user menu, order-device popups, the playlist and schedule
 * headers). They had drifted: some measured once on mount, which goes stale the moment a
 * container resizes. Reach for this rather than writing an eleventh.
 *
 * Radix rather than the native `title` attribute, which fires regardless of clipping and has a
 * fixed ~1s delay that cannot be tuned.
 */
export function TruncatedTooltip({
  text,
  className,
  delayDuration = 300,
  side = 'top',
  align,
  children,
  onClick,
  'data-testid': testId,
}: TruncatedTooltipProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(false);
            return;
          }
          // Measured at the moment it WOULD open, not on mount. The element's width depends on
          // its container's, which changes with the window, with a sidebar resize, and with
          // siblings appearing beside it — so a width captured earlier is a width that lies.
          // Not truncated means it simply never opens.
          const el = ref.current;
          setOpen(el ? el.scrollWidth > el.clientWidth : false);
        }}
      >
        <TooltipTrigger asChild>
          {/* min-w-0: the trigger is a flex child on most rows, and without it the span refuses
              to shrink below its content, so `truncate` never engages and nothing ever clips. */}
          <span
            ref={ref}
            data-testid={testId}
            className={cn('min-w-0 truncate', className)}
            onClick={onClick}
          >
            {children ?? text}
          </span>
        </TooltipTrigger>
        {/* break-words, not break-all: a long NAME should wrap between words where it can. */}
        <TooltipContent side={side} align={align} className="max-w-sm break-words">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
